<template>
  <div class="booking-container">
    <div class="booking" :key="reloadKey" v-if="configuration">
      <h1 tabindex="0" ref="firstStep">{{messages.applicationTitle}}</h1>
      <v-expansion-panel class="booking-wrapper" v-model="currentExpansionPanelOpen" ref="processStepsRef" v-show="loaded">
        <component v-for="(step, index) in steps"
          :is="step.compStep"
          :key="index"
          v-show="step.show"
          :is-first="step.isFirst"
          :disabled="step.disabled"
          :process-number="step.processNumber"
          @data-selected="dataSelected"
          @check-steps="checkSteps"
          :load-calendar="loadCalendar"
          :display-selected-date-time="displaySelectedDateTime"
          @:update:display-selected-date-time="displaySelectedDateTime = $event"
          :load-mobile-verification="loadMobileVerification"
          :load-appointment-list.sync="loadAppointmentList"
          @reset-data="resetData"
          :start-page="startPage"
          :url-params="urlParams"
          @is-loaded='putLoadedOn'
          :id="'step'+ (index + 1)"
          :webid="step.compStep"
        ></component>
      </v-expansion-panel>
      <v-container fluid v-if="!loaded">
        <v-layout align-center justify-center row fill-height>
          <v-progress-circular
              indeterminate
              :size="70"
              color="primary"
            ></v-progress-circular>
        </v-layout>
      </v-container>
    </div>
  </div>
</template>

<script>
import RestService from '../services/restService'
import DataService from '../services/dataService'
import ServiceSelection from '../components/ServiceSelection'
import BranchSelection from '../components/BranchSelection'
import DateTimeSelectionStep from '../components/DateTimeSelectionStep'
import ContactStep from '../components/ContactStep'
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'
import { eventHub } from '../utils/eventhub'

export default {
  name: 'Booking',
  props: [
    'startPage',
    'urlParams',
    'reloadKey',
    'focusEventKey'
  ],
  mixins: [RestService, DataService],
  components: {
    ServiceSelection,
    BranchSelection,
    DateTimeSelectionStep,
    ContactStep
  },
  data: () => ({
    currentExpansionPanelOpen: 0,
    displaySelectedDateTime: false,
    loadCalendar: false,
    loadMobileVerification: false,
    loadAppointmentList: false,
    steps: [
      {
        compStep: null,
        show: true,
        isFirst: true,
        disabled: false,
        processNumber: 1
      }, {
        compStep: null,
        show: true,
        isFirst: false,
        disabled: true,
        processNumber: 2
      }, {
        compStep: null,
        show: true,
        isFirst: null,
        disabled: true,
        processNumber: 3
      }, {
        compStep: null,
        show: true,
        isFirst: null,
        disabled: true,
        processNumber: 4
      }
    ],
    loaded: false
  }),
  computed: {
    ...mapState([
      'isServicesStepValid',
      'isBranchStepValid',
      'configuration',
      'selectedServices',
      'selectedBranch',
      'servicesTranslations',
      'branchTranslations',
      'selectedDate',
      'selectedTimeSlot',
      'contactDetails',
      'reservedAppointmedId',
      'preSelectedServices',
      'messages',
      'funcDebug'
    ]),
    ...mapGetters({
      totalCost: 'selectedServicesTotalCost',
      customSlotLength: 'customSlotLength',
      isDateTimeSelectionStepValid: 'isDateTimeSelectionStepValid'
    })
  },
  watch: {
    focusEventKey () {
      if (this.funcDebug) console.log('Booking:watch:focusEventKey')
      this.$refs.firstStep.focus()
    }
  },
  methods: {
    ...mapMutations([
      'setPreSelectedBranch',
      'setPreSelectedServices',
      'setPreSelectedServiceGroups'
    ]),
    ...mapActions([
      'resetSelectedDateAndTimeSlot',
      'resetBookingStore'
    ]),
    checkSteps () {
      if (this.funcDebug) console.log('Booking:methods:checkSteps')
      this.steps[0].disabled = false
      // check validation of first step
      if ((this.steps[0].compStep === 'serviceSelection' && !this.isServicesStepValid) ||
        (this.steps[0].compStep === 'branchSelection' && !this.isBranchStepValid)) {
        // not valid then hide all but step one
        this.steps[1].disabled = true
        this.steps[2].disabled = true
        this.steps[3].disabled = true
        this.loadCalendar = false
        this.loadMobileVerification = false
        this.displaySelectedDateTime = false
        /* if(this.steps[0].compStep === 'serviceSelection' && !this.isServicesStepValid) { */
        this.currentExpansionPanelOpen = 0
        /* } */
      } else {
        // check validation of second step
        if ((this.steps[1].compStep === 'serviceSelection' && !this.isServicesStepValid) ||
          (this.steps[1].compStep === 'branchSelection' && !this.isBranchStepValid)) {
          // not valid then hide all after step 2
          this.steps[1].disabled = false
          this.steps[2].disabled = true
          this.steps[3].disabled = true
          this.loadCalendar = false
          this.loadMobileVerification = false
          this.displaySelectedDateTime = false
          if (this.steps[0].compStep === 'serviceSelection') {
            // The first step is service selection, it has a valid service selection, but
            // the services selection panel should still stay open as the customer
            // might want to add more adults/children/services
            this.currentExpansionPanelOpen = 0
          } else {
            this.currentExpansionPanelOpen = 1
          }
        } else {
          // check validity of third step
          if (!this.isDateTimeSelectionStepValid) {
            this.steps[3].disabled = true
          }
          this.steps[1].disabled = false
          this.steps[2].disabled = false
          this.loadCalendar = true
          if (this.steps[1].compStep === 'serviceSelection') {
            // The second step is service selection, and it is valid. But it might still be the case
            // that the customer wants to add more adults/children/services, so leavie it expaneded.
            this.currentExpansionPanelOpen = 1
          } else {
            this.currentExpansionPanelOpen = 2
          }
          // eventHub.$emit('date-time-step-focus');
        }
      }
    },
    dataSelected (componentType) {
      if (this.funcDebug) console.log('Booking:methods:dataSelected')
      if (componentType === 'branch' || componentType === 'services') {
        this.resetSelectedDateAndTimeSlot()
        this.checkSteps()
      } else if (componentType === 'date-time') {
        this.steps[3].disabled = false
        this.displaySelectedDateTime = true
        this.loadMobileVerification = true
        this.currentExpansionPanelOpen = 3
      } else { }
    },
    checkUrlParams () {
      if (this.funcDebug) console.log('Booking:methods:checkUrlParams')
      switch (this.$route.name) {
        case 'servicebooking':
          // services preselected
          this.steps[0].compStep = 'serviceSelection'
          this.steps[1].compStep = 'branchSelection'
          if (this.$route.params.serviceId.split(',').length === 1) {
            // one service, should we show it, check the option
            if (this.configuration && !this.configuration.showSingleService) {
              // don't show it
              this.steps[0].show = false
              this.steps[1].processNumber = 1
              this.steps[2].processNumber = 2
              this.steps[3].processNumber = 3
            }
          }
          // get the preselected services
          this.setPreSelectedServices(this.$route.params.serviceId.split(','))
          break
        case 'branchbooking':
          // branch preselected
          this.steps[0].compStep = 'branchSelection'
          this.steps[1].compStep = 'serviceSelection'
          // check if we need to show the branch
          if (this.configuration && !this.configuration.showSingleBranch) {
            // don't show it
            this.steps[0].show = false
            this.steps[1].processNumber = 1
            this.steps[2].processNumber = 2
            this.steps[3].processNumber = 3
          }
          // get the preselected branch
          this.setPreSelectedBranch(this.$route.params.branchId)
          break
        case 'calendarbooking':
          if (this.configuration && this.configuration.serviceFirst) {
            // service is first
            this.steps[0].compStep = 'serviceSelection'
            this.steps[1].compStep = 'branchSelection'
            // check if we need to display service
            if (this.configuration && !this.configuration.showSingleService && this.$route.params.serviceId.split(',').length === 1) {
              this.steps[0].show = false
              this.steps[1].processNumber = 1
              this.steps[2].processNumber = 2
              this.steps[3].processNumber = 3
            }
            // check if we need to display branches
            if (this.configuration && !this.configuration.showSingleBranch) {
              this.steps[1].show = false
              this.steps[2].processNumber = 2
              this.steps[3].processNumber = 3
            }
          } else {
            // branch is first
            this.steps[0].compStep = 'branchSelection'
            this.steps[1].compStep = 'serviceSelection'
            // check if we need to display branches
            if (this.configuration && !this.configuration.showSingleBranch) {
              this.steps[0].show = false
              this.steps[1].processNumber = 1
              this.steps[2].processNumber = 2
              this.steps[3].processNumber = 3
            }
            // check if we need to display service
            if (this.configuration && !this.configuration.showSingleService && this.$route.params.serviceId.split(',').length === 1) {
              this.steps[1].show = false
              this.steps[2].processNumber = 2
              this.steps[3].processNumber = 3
            }
          }
          // what if both steps are off
          if (!(this.steps[0].show || this.steps[1].show)) {
            this.steps[0].compStep = 'branchSelection'
            this.steps[1].compStep = 'serviceSelection'
            this.steps[2].processNumber = 1
            this.steps[3].processNumber = 2
          }
          // services and branch preselected
          this.setPreSelectedBranch(this.$route.params.branchId)
          this.setPreSelectedServices(this.$route.params.serviceId.split(','))
          break
        case 'servicegroupsbooking':
          if (this.configuration && this.configuration.serviceFirst) {
            this.steps[0].compStep = 'serviceSelection'
            this.steps[1].compStep = 'branchSelection'
          } else {
            this.steps[0].compStep = 'branchSelection'
            this.steps[1].compStep = 'serviceSelection'
          }
          // servicegroup preselected
          this.setPreSelectedServiceGroups(this.$route.params.serviceGroups.split(','))
          break
        case 'branchandservicegroupsbooking':
          // always set branch to first step in this case, as one branch is preselected
          this.steps[0].compStep = 'branchSelection'
          this.steps[1].compStep = 'serviceSelection'
          // check if we need to display branches
          if (this.configuration && !this.configuration.showSingleBranch) {
            this.steps[0].show = false
            this.steps[1].processNumber = 1
            this.steps[2].processNumber = 2
            this.steps[3].processNumber = 3
          }
          // branch preselected
          this.setPreSelectedBranch(this.$route.params.branchId)
          // servicegroup preselected
          this.setPreSelectedServiceGroups(this.$route.params.serviceGroups.split(','))
          break
        default:
          if (this.configuration && this.configuration.serviceFirst) {
            this.steps[0].compStep = 'serviceSelection'
            this.steps[1].compStep = 'branchSelection'
          } else {
            this.steps[0].compStep = 'branchSelection'
            this.steps[1].compStep = 'serviceSelection'
          }
          break
      }
      this.steps[2].compStep = 'dateTimeSelectionStep'
      this.steps[3].compStep = 'contactStep'
    },
    resetData () {
      if (this.funcDebug) console.log('Booking:methods:resetData')
      this.panel = []
      this.resetBookingStore()
      Object.assign(this.$data, this.$options.data.call(this))
      this.checkUrlParams()
      this.checkSteps()
      // force reload the complete form
      this.$emit('increase-reload-key')
      // remove all messages
      eventHub.$emit('hide-message')
    },
    putLoadedOn () {
      if (this.funcDebug) console.log('Booking:methods:putLoadedOn')
      this.loaded = true
    }
  },
  mounted () {
    if (this.funcDebug) console.log('Booking:mounted')
    this.loaded = false
    this.checkUrlParams()
  }

}
</script>

<style lang="scss">
.booking h1 {
  padding-bottom: 24px;
  background-color: var(--wrapper-bg-color-primary) !important;
  font-weight:300;
}

.booking-wrapper {
  box-shadow: none;
  background-color: var(--wrapper-bg-color-primary) !important;
}

.theme--light.v-expansion-panel
  .v-expansion-panel__container.process-step-header {
  background-color: var(--primary-color) !important;
  color: var(--primary-bg-text-color) !important;
  margin-top: 4px;
  margin-bottom: 4px;
}

.booking-wrapper .process-step-header .process-step-name {
  text-transform: uppercase;
  line-height: 40px;
  padding-left: 24px;
  font-size: 24px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  color: var(--process-step-name-text-color);
  width: 90%;

  @at-root [dir="rtl"] & {
    padding-left: 0;
    padding-right: 24px;
  }
}

.process-step-header
  .v-expansion-panel__header
  .v-expansion-panel__header__icon
  .v-icon.material-icons.theme--light {
  color: var(--primary-bg-text-color) !important;
  font-size: 36px;
}

.booking-wrapper
  .v-expansion-panel__container.process-step-header.v-expansion-panel__container:focus {
  background-color: var(--focused-element) !important;
}
.booking-wrapper
  .v-expansion-panel__container.process-step-header.v-expansion-panel__container:focus
  .process-step-name {
  text-decoration: underline;
}
.booking-wrapper
  .v-expansion-panel__container.process-step-header.v-expansion-panel__container--disabled {
  background-color:var(--not-focused-element) !important;
}

.booking-wrapper
  .v-expansion-panel__container.process-step-header.v-expansion-panel__container--disabled .process-number{
    background-color: var(--not-focused-process-step-number-bg) !important;;
  }

.booking-wrapper
  .v-expansion-panel__container.process-step-header.v-expansion-panel__container--active {
  background-color: var(--focused-element) !important;
}

.booking-container{
    padding: 16px;
    background-color: var(--wrapper-bg-color-primary);
  }

.choose-wrapper {
  padding: 0 24px 0 8px;

  @at-root [dir="rtl"] & {
    text-align: right;
  }
}

.chosen-options {
  /*position: absolute;
  right: 75px;
  bottom: 24px;*/
  margin-left: 65px;

  @at-root [dir="rtl"] & {
    margin-right: 65px;
  }
}

.process-number {
  font-size: 24px;
  background-color: var(--process-step-number-bg) !important;
  line-height: 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  text-align: center;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  text-align: center;
  color: var(--process-step-number-text-color);
}

.service-total-cost {
  font-weight: 700;
  position: absolute;
  bottom: 16px;
  right: 24px;

  @at-root [dir="rtl"] & {
    right: auto;
    left: 24px;
  }
}

.amount-of {
  width: 150px;
  display: flex;
}

.theme--light.v-expansion-panel
  .v-expansion-panel__container.service-expansion-panel {
  color: var(--default-font-color) !important;
}

/*.theme--light.v-expansion-panel
  .v-expansion-panel__container.service-expansion-panel:focus
  .v-expansion-panel__header
  .v-expansion-panel__header__icon
  .v-icon.material-icons.theme--light {
  color: var(--focused-element) !important;
}
*/

/*
.theme--light.v-expansion-panel
  .v-expansion-panel__container.service-expansion-panel:focus
  .v-expansion-panel__header {
  color: var(--focused-element) !important;
}
*/

.service-expansion-panel
  .v-expansion-panel__header
  .v-expansion-panel__header__icon
  .v-icon.material-icons.theme--light {
  font-size: 24px;
  color: var(--default-font-color) !important;
}

.services-wrapper .service-expansion-panel .service-name,
.services-wrapper .service-expansion-panel .service-name .v-input__slot,
.services-wrapper
  .service-expansion-panel
  .v-input--selection-controls
  .v-input__slot {
  margin: 0;
  padding: 0;
  flex: none;
}

.services-wrapper
  .service-expansion-panel
  .service-name
  .v-input__control
  .v-messages,
.services-wrapper .service-expansion-panel .v-input__control .v-messages {
  display: none;
}

.services-wrapper
  .service-expansion-panel
  .service-name
  .v-icon.material-icons.theme--light.accent--text,
form .info-text .v-icon.material-icons.theme--light.accent--text {
  color: var(--focused-element) !important;
}

form {
  padding: 16px 0;
}

.v-input.input-field.v-text-field.phone-input {
  max-width: 420px;

  min-height: 84px;
  padding-top: 0px !important;
  position: relative;

  @at-root [dir="rtl"] & {
    margin-left: 24px;
    margin-right: 244px;
  }
}

.v-input.input-field.v-text-field.countrycode-input {
  max-width: 110px;
  min-height: 84px;
  padding-top: 0px !important;
  position: relative;

  @at-root [dir="rtl"] & {
    margin-left: 24px;
    margin-right: 244px;
  }
}

.flex {
  display: flex;
}

.total-cost {
  font-weight: 700;
}

.info-text-strong {
    font-family: CustomQWBFont, Roboto;
    font-size: 20px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: 0.25px;
    color: var(--default-font-color);
}

.v-input.input-field.v-text-field {
  margin: 0 0 0 24px;
  width: 550px;

  @at-root [dir="rtl"] & {
    margin: 0 24px 0 0;
  }
}

.v-text-field .v-label {
  top: 10px;
}

.fields-wrapper {
  position: relative;
}

.v-alert.v-alert {
  border: none;
  width: 100%;
  margin-bottom: 0;
}

.v-alert {
  > div {
    @at-root [dir="rtl"] & {
      text-align: right;
    }
  }
  &__icon {
    @at-root [dir="rtl"] & {
      margin-right: 0;
      margin-left: 16px;
    }
  }
}

.fields-wrapper .phone-verification-btn.v-btn {
  position: absolute;
  left: 574px;
  height: 56px;
  background-color: var(--primary-btn-bg-color) !important;
  font-weight: 400;
  color: var(--primary-btn-text-color) !important;
  top: 0;
  margin-top: 0;
  margin-right: 24px;

  @at-root [dir="rtl"] & {
    left: auto;
    right: 574px;
    margin-right: 8px;
    margin-left: 24px;
  }
}

.v-input--selection-controls.v-input .v-label {
  font-weight: normal;
  letter-spacing: 0.5px;
  color: var(--default-font-color);
}

form .submit.v-btn.v-btn--flat.theme--light {
  margin: 10px 24px;

  @at-root [dir="rtl"] & {
    display: flex;
  }
}
.reset-btn.v-btn.v-btn--icon.theme--light {
  position: absolute;
  top: 0;
  right: 0;
  margin: 16px 24px 0 0;
  font-weight: 400;
  font-size: 17px;
  width: auto;
  margin: 16px 24px 0 0;
  padding: 0 16px;
  background-color:var(--reset-btn-bg-color) !important;
  color: var(--reset-btn-text-color) !important;
  border: 1px solid var(--reset-btn-border-color) !important;
  border-radius: 0;
  height: 44px;
  @at-root [dir="rtl"] & {
    left: 0;
    right: auto;
    margin: 16px 0 0 24px;
  }
}

.reset-btn.v-btn.v-btn--icon.theme--light:hover{
  background-color:initial !important;
}

.notification-type {
  padding: 10px 24px;
}

@media only screen and (max-width: 599px) {
  @media only screen and (max-width: 359px) and (min-width: 300px) {
    .process-number {
      line-height: 35px;
      height: 35px;
      width: 35px;
    }

    .v-card__text.flex {
      display: block;
    }

    .booking-wrapper .process-step-header .process-step-name {
      font-size: 16px;
      line-height: 35px;
      padding-left: 10px;

      @at-root [dir="rtl"] & {
        padding-left: 0;
        padding-right: 10px;
      }
    }
  }

  .booking h1 {
    padding-bottom: 16px;
  }

  .v-expansion-panel__header {
    padding: 12px;
  }

  .choose-wrapper {
    padding: 0;
    padding-bottom: 20px;
  }

  .amount-of {
    width: auto;
  }

  .amount-of .number {
    min-width: 35px;
  }

  .service-total-cost {
    bottom: 0;
    right: 16px;

    @at-root [dir="rtl"] & {
      right: auto;
      left: 16px;
    }
  }

  .chosen-options {
    /*position: static;*/
  }

  .info-text {
    padding: 8px;
  }

  .v-input.input-field.v-text-field {
    padding: 0 16px;
    width: 100%;
    margin: 0;

    @at-root [dir="rtl"] & {
      margin: 0;
    }
  }

  .v-text-field .v-label {
    font-size: 12px;
    height: 40px;
    white-space: normal;
    top:6px;
    max-width: 100%;
  }

  .v-input.input-field.v-text-field.phone-input {
    width: 100%;
    position: static;
    margin: 0;

    @at-root [dir="rtl"] & {
      margin-left: 0;
      margin-right: 0;
    }
  }

  .info-text.agreement {
    width: 100%;
    padding-top: 30px;
  }

  form {
    padding: 0 0 16px 0;
  }
  .reset-btn.v-btn.v-btn--icon.theme--light {
    margin-right: 16px;
    font-size: 16px;
    @at-root [dir="rtl"] & {
      left: 0;
      right: auto;
      margin: 16px 0 0 16px;
    }
  }

  form .submit.v-btn.v-btn--flat.theme--light {
    display: block;
    width: auto;
    margin: 0 auto;
  }

  .services-wrapper
    .service-expansion-panel
    .v-input--radio-group--column
    .v-radio.service-name {
    width: auto;
    margin-left: 16px;
  }

  .fields-wrapper .phone-verification-btn.v-btn {
    position: relative;
    display: block;
    margin-left: 16px;
    height: 52px;
    left: auto;

    @at-root [dir="rtl"] & {
      margin-left: 8px;
      margin-right: 16px;
      right: auto;
    }
  }
}

@media screen and (min-width: 600px) and (max-width: 751px) {
  .fields-wrapper .phone-verification-btn.v-btn {
    position: relative;
    display: block;
    margin-left: 24px;
    left: auto;

    @at-root [dir="rtl"] & {
      margin-left: 8px;
      margin-right: 24px;
      right: auto;
    }
  }

  .custom-field{
      width: auto !important;
      margin: 0 24px 0 24px !important;
  }

  .v-input.input-field.v-text-field {
    margin: 0 24px 0 24px;
    width: auto;

    @at-root [dir="rtl"] & {
      margin: 0 24px 0 24px;
    }
  }

  .v-input.input-field.v-text-field.phone-input {
    width: auto;
  }
}
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  /* IE10+ CSS styles go here */
  .fields-wrapper .v-input .v-input__control {
    width: auto;
  }

  .agreement .v-input__control{
    width: 100% !important;
  }

  .v-input{
    display: block;
  }

  .v-text-field .v-label--active{
    width: 90%;
  }
}

</style>
